/**
 * CList Types
 *
 * Shared types for the CList component and its variants.
 * Uses a discriminated union pattern to ensure type safety based on the `variant` prop.
 */
"use client";

import React from "react";

// ============================================================================
// BADGE VARIANTS
// ============================================================================

/** Badge color variants for Activity items (maps to CBadge variants) */
export type ActivityBadgeVariant =
  | "amber"
  | "indigo"
  | "indigo-solid"
  | "blue"
  | "emerald"
  | "pink"
  | "violet"
  | "teal"
  | "red";

/** Badge variants for Avatar items */
export type AvatarBadgeVariant =
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "neutral";

/** Status variants for Status items */
export type StatusVariant =
  | "urgent"
  | "pending"
  | "new"
  | "success"
  | "warning"
  | "danger"
  | "info";

// ============================================================================
// ITEM TYPES
// ============================================================================

/**
 * Activity item - icon-based with points
 *
 * Use for: tasks, achievements, challenges, rewards, activities
 */
export interface ActivityItemType {
  /** Unique identifier for the item */
  id: string;
  /** Main title text */
  title: string;
  /** Secondary text (description, timestamp) */
  subtitle?: string;
  /** Badge configuration */
  badge: {
    /** Badge text (e.g., "WEEKLY", "NEW", "+25 PTS") */
    text: string;
    /** Color variant */
    variant: ActivityBadgeVariant;
  };
  /** Points value. Set to 0 or undefined to hide */
  points?: number;
  /** Override points text color (Tailwind class) */
  pointsColor?: string;
  /** Custom icon component */
  icon?: React.ReactNode;
  /** Predefined icon key (resolved at render time) */
  iconKey?:
    | "star"
    | "trophy"
    | "graduation"
    | "trending"
    | "heart"
    | "calendar"
    | "users"
    | "zap"
    | "check";
  /** Background color for icon container (Tailwind class) */
  iconBgColor: string;
  /** Icon color (Tailwind class) */
  iconColor: string;
}

/**
 * Avatar item - initials-based with timestamp
 *
 * Use for: team members, users, contacts, participants
 */
export interface AvatarItemType {
  /** Unique identifier */
  id?: string;
  /** Person/item name */
  title: string;
  /** Role, description, or secondary info */
  subtitle?: string;
  /** 1-2 character initials for avatar */
  initials: string;
  /** Avatar color (Tailwind classes, default: "bg-primary/10 text-primary") */
  initialsColor?: string;
  /** Optional status badge */
  badge?: {
    /** Badge text */
    label: string;
    /** Color variant */
    variant?: AvatarBadgeVariant;
  };
  /** Time indicator (e.g., "2 hours ago", "Yesterday") */
  timestamp?: string;
  /** Click handler */
  onClick?: () => void;
}

/**
 * Status item - left-bordered with status badge
 *
 * Use for: orders, tickets, requests, tasks with status
 */
export interface StatusItemType {
  /** Unique identifier */
  id?: string;
  /** Item title */
  title: string;
  /** Description or details */
  subtitle?: string;
  /** Status type (determines border and badge color) */
  status: StatusVariant;
  /** Text shown in status badge */
  statusLabel: string;
  /** Click handler */
  onClick?: () => void;
}

// ============================================================================
// COMPONENT PROPS (Discriminated Union)
// ============================================================================

/** Base props shared by all variants */
interface BaseListProps {
  /** Additional CSS classes */
  className?: string;
  /** Show dividers between items (default: true) */
  showDivider?: boolean;
}

/** Activity variant props */
export interface ActivityListProps extends BaseListProps {
  variant: "activity";
  items: ActivityItemType[];
}

/** Avatar variant props */
export interface AvatarListProps extends BaseListProps {
  variant: "avatar";
  items: AvatarItemType[];
}

/** Status variant props */
export interface StatusListProps extends BaseListProps {
  variant: "status";
  items: StatusItemType[];
}

/** Union type for CList props - TypeScript enforces correct items based on variant */
export type CListProps = ActivityListProps | AvatarListProps | StatusListProps;

// ============================================================================
// STYLE CONSTANTS
// ============================================================================

/**
 * Pre-defined icon background and color combinations for Activity items.
 * Use these to ensure proper dark mode support.
 *
 * @example
 * const item = {
 *   ...ACTIVITY_ICON_STYLES.amber,
 *   // Results in: iconBgColor: "bg-amber-100 dark:bg-amber-900/30", iconColor: "text-amber-600 dark:text-amber-400"
 * }
 */
export const ACTIVITY_ICON_STYLES = {
  amber: {
    bgColor: "bg-amber-100 dark:bg-amber-900/30",
    iconColor: "text-amber-600 dark:text-amber-400",
  },
  indigo: {
    bgColor: "bg-indigo-100 dark:bg-indigo-900/30",
    iconColor: "text-indigo-600 dark:text-indigo-400",
  },
  emerald: {
    bgColor: "bg-emerald-100 dark:bg-emerald-900/30",
    iconColor: "text-emerald-600 dark:text-emerald-400",
  },
  pink: {
    bgColor: "bg-pink-100 dark:bg-pink-900/30",
    iconColor: "text-pink-600 dark:text-pink-400",
  },
  violet: {
    bgColor: "bg-violet-100 dark:bg-violet-900/30",
    iconColor: "text-violet-600 dark:text-violet-400",
  },
  teal: {
    bgColor: "bg-teal-100 dark:bg-teal-900/30",
    iconColor: "text-teal-600 dark:text-teal-400",
  },
  red: {
    bgColor: "bg-red-100 dark:bg-red-900/30",
    iconColor: "text-red-500 dark:text-red-400",
  },
  blue: {
    bgColor: "bg-blue-100 dark:bg-blue-900/30",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
} as const;

/** Avatar badge color configuration */
export const AVATAR_BADGE_CONFIG: Record<AvatarBadgeVariant, string> = {
  success:
    "bg-emerald-100 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400",
  warning: "bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-400",
  danger: "bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400",
  info: "bg-blue-100 text-blue-600 dark:bg-blue-950 dark:text-blue-400",
  neutral: "bg-muted text-muted-foreground",
};

/** Status color configuration (border and badge) */
export const STATUS_CONFIG: Record<
  StatusVariant,
  { borderColor: string; badgeClass: string }
> = {
  urgent: {
    borderColor: "bg-red-500",
    badgeClass: "bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400",
  },
  danger: {
    borderColor: "bg-red-500",
    badgeClass: "bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400",
  },
  pending: {
    borderColor: "bg-amber-500",
    badgeClass:
      "bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-400",
  },
  warning: {
    borderColor: "bg-amber-500",
    badgeClass:
      "bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-400",
  },
  new: {
    borderColor: "bg-blue-500",
    badgeClass: "bg-blue-100 text-blue-600 dark:bg-blue-950 dark:text-blue-400",
  },
  info: {
    borderColor: "bg-blue-500",
    badgeClass: "bg-blue-100 text-blue-600 dark:bg-blue-950 dark:text-blue-400",
  },
  success: {
    borderColor: "bg-emerald-500",
    badgeClass:
      "bg-emerald-100 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400",
  },
};
